<script setup lang="ts">
type SourceButtonTheme = 'light' | 'dark'
type SourceButtonKind =
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'add'
    | 'deletePrimary'
    | 'deleteSecondary'
    | 'deleteTertiary'
type SourceButtonSize = 'xsmall' | 'small' | 'medium' | 'large'

interface SourceButtonProps {
  text?: string
  theme?: SourceButtonTheme
  type?: SourceButtonKind
  size?: SourceButtonSize
  icon?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<SourceButtonProps>(), {
  text: '',
  theme: 'light',
  type: 'primary',
  size: 'small',
  icon: undefined,
  disabled: false
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const onClick = (event: MouseEvent) => {
  if (props.disabled) {
    event.preventDefault()
    return
  }
  emit('click', event)
}
</script>

<template>
  <button
      class="source-button"
      type="button"
      :disabled="disabled"
      :aria-disabled="disabled || undefined"
      :class="[type, size, theme, { disabled, 'icon-only': !text && (!!icon || !!$slots.icon) }]"
      @click="onClick"
  >
    <slot name="icon">
      <span v-if="icon" class="material-symbols-rounded">
        {{ icon }}
      </span>
    </slot>

    <span v-if="text">
      {{ text }}
    </span>
  </button>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/index" as *;
@use "~/assets/scss/app" as *;

// base
.source-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  border: none;
  background: transparent;

  &:hover:not(:disabled) {
    cursor: pointer;
  }

  &:disabled,
  &.disabled {
    background: c.$enedis-neutral-40;
    color: c.$enedis-neutral-100;
    border: none;
    cursor: not-allowed;
  }

  &.icon-only {
    // léger ajustement si tu veux un bouton juste icône
    padding-inline: 8px;
  }
}

// type
.primary {
  background: var(--color-primary);
  color: var(--color-text-on-primary);

  &:hover:not(:disabled) {
    background: var(--color-primary-hover);
  }

  &:active:not(:disabled) {
    background: var(--color-primary-active);
  }

  &.dark {
    background: c.$enedis-interface-bleufonce-400;
  }
}

.secondary {
  border: 1px solid var(--color-primary);
  color: var(--color-primary);
  background: var(--color-bg-elevated);

  &:hover:not(:disabled) {
    background: var(--color-bg-hover-primary);
  }

  &:active:not(:disabled) {
    background: var(--color-bg-active-primary);
  }

  &:focus-visible:not(:disabled) {
    outline: 2px solid var(--color-primary-hover);
    outline-offset: 2px;
  }
}

.tertiary {
  background: var(--color-bg-elevated);
  color: var(--color-primary);

  &:hover:not(:disabled) {
    background: var(--color-bg-hover-primary);
  }

  &:active:not(:disabled) {
    background: var(--color-bg-active-primary);
  }

  &:focus-visible:not(:disabled) {
    outline: 2px solid var(--color-primary-hover);
    outline-offset: 2px;
  }
}

.add {
  border: 2px dashed var(--color-primary);
  color: var(--color-primary);
  background: transparent;

  &:hover:not(:disabled) {
    border-color: var(--color-primary-active);
    color: var(--color-primary-active);
    background: var(--color-bg-hover-primary);
  }

  &:active:not(:disabled) {
    border-color: var(--color-primary-active);
    background: var(--color-bg-active-primary);
  }

  &:focus-visible:not(:disabled) {
    outline: 2px solid var(--color-primary-hover);
    outline-offset: 2px;
  }

  &.icon-only .material-symbols-rounded {
    font-size: 24px;
  }
}

.deletePrimary {
  background: var(--color-danger);
  color: var(--color-text-on-primary);

  &:hover:not(:disabled) {
    background: var(--color-danger-hover);
  }

  &:active:not(:disabled) {
    background: c.$enedis-error-600;
  }
}

.deleteSecondary {
  border: 1px solid var(--color-danger);
  color: var(--color-danger);
  background: var(--color-bg-elevated);

  &:hover:not(:disabled) {
    background: var(--color-danger-bg);
  }

  &:active:not(:disabled) {
    background: c.$enedis-error-300;
  }
}

.deleteTertiary {
  background: var(--color-bg-elevated);
  color: var(--color-danger);

  &:hover:not(:disabled) {
    background: var(--color-danger-bg);
  }

  &:active:not(:disabled) {
    background: c.$enedis-error-300;
  }
}

// size
.xsmall {
  padding: 8px 12px;
  min-height: 36px;
  @extend .text-2xs;

  span {
    @extend .text-2xs;
  }
}

.small {
  padding: 12px 14px;
  min-height: 44px;
  @extend .text-xs;
}

.medium {
  padding: 14px 16px;
  min-height: 48px;
  @extend .text-s;
}

.large {
  padding: 16px 24px;
  min-height: 56px;
  @extend .text-m;
}
</style>
